import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Trash2, Pencil, Check, X, ChevronUp, ChevronDown, Lock } from 'lucide-react';
import { classificationsApi } from '../../api/classifications';
import { useCompany } from '../../contexts/CompanyContext';
import EmptyState from '../EmptyState';
import type { ClassificationDef, ClassificationKind } from '../../types';

// The editable pick-lists: five on a ticket, three in the CRM. Status and
// Category have their own richer tabs and deliberately do not appear here.
const KINDS: { kind: ClassificationKind; label: string; blurb: string; placeholder: string }[] = [
  {
    kind: 'type',
    label: 'Type',
    blurb: 'What kind of request this is. Every ticket has one.',
    placeholder: 'e.g. Onboarding, Change request, Outage…',
  },
  {
    kind: 'priority',
    label: 'Priority',
    blurb: 'How urgent the ticket is. SLA policies set their response and resolution targets per priority, so removing one that a policy uses is refused — turn it off instead.',
    placeholder: 'e.g. Emergency, Scheduled…',
  },
  {
    kind: 'source',
    label: 'Source',
    blurb: 'Where the ticket came from. Portal, Email, API and In app are set by ctTicketing itself when a ticket arrives that way — you can rename or hide them, but not delete them.',
    placeholder: 'e.g. Site visit, Text message…',
  },
  {
    kind: 'impact',
    label: 'Business impact',
    blurb: 'Optional — how widely the issue is felt.',
    placeholder: 'e.g. Single site, Region…',
  },
  {
    kind: 'account_status',
    label: 'Account status',
    blurb: 'Where an account relationship stands. Active is set by ctTicketing when an account is created, so it can be renamed but not deleted.',
    placeholder: 'e.g. Pilot, Renewal due…',
  },
  {
    kind: 'lead_source',
    label: 'Lead source',
    blurb: 'How a relationship started. Inbound email, Contact form and Outreach are set automatically when a conversation arrives that way; the rest are yours.',
    placeholder: 'e.g. Trade show, Partner…',
  },
  {
    kind: 'contact_role',
    label: 'Contact role',
    blurb: 'What a person does on an account or a deal. A contact can hold different roles in different places.',
    placeholder: 'e.g. Scheduler, Operations lead…',
  },
  {
    kind: 'environment',
    label: 'Environment',
    blurb: 'Optional — which environment the issue is in.',
    placeholder: 'e.g. Training, DR site…',
  },
];

const TONE_OPTIONS = [
  { value: 'muted',   label: 'Gray' },
  { value: 'primary', label: 'Blue (brand)' },
  { value: 'ok',      label: 'Green' },
  { value: 'warning', label: 'Amber' },
  { value: 'error',   label: 'Red' },
];

const toneCss = (color: string) =>
  color.startsWith('#')
    ? color
    : `var(--scpa-${color === 'primary' ? 'primary' : color === 'muted' ? 'text-muted' : `status-${color}`})`;

interface FormState {
  label: string;
  color: string;
  isDefault: boolean;
}

const EMPTY_FORM: FormState = { label: '', color: 'muted', isDefault: false };

export default function ClassificationsTab() {
  const queryClient = useQueryClient();
  const { currentCompany } = useCompany();
  const companyId = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;

  const [kind, setKind] = useState<ClassificationKind>('type');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const { data: all = [], isLoading } = useQuery({
    queryKey: ['classifications', companyId],
    queryFn: () => classificationsApi.list(),
    enabled: !!currentCompany,
  });

  const meta = KINDS.find(k => k.kind === kind)!;
  const rows = all
    .filter(d => d.kind === kind)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label));

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['classifications'] });
  const reset = () => { setAdding(false); setEditingId(null); setForm(EMPTY_FORM); };

  const createMutation = useMutation({
    mutationFn: () => classificationsApi.create({ kind, ...form }),
    onSuccess: () => { invalidate(); reset(); toast.success(`${meta.label} added`); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e.message),
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; data: Partial<ClassificationDef> }) =>
      classificationsApi.update(vars.id, vars.data),
    onSuccess: () => { invalidate(); setEditingId(null); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => classificationsApi.delete(id),
    onSuccess: () => { invalidate(); toast.success(`${meta.label} deleted`); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e.message),
  });

  const startEdit = (d: ClassificationDef) => {
    setAdding(false);
    setEditingId(d.id);
    setForm({ label: d.label, color: d.color, isDefault: d.isDefault });
  };

  const saveEdit = (d: ClassificationDef) => {
    if (!form.label.trim()) return toast.error('Label is required');
    updateMutation.mutate({ id: d.id, data: form });
  };

  const move = (d: ClassificationDef, dir: -1 | 1) => {
    const idx = rows.findIndex(x => x.id === d.id);
    const swap = rows[idx + dir];
    if (!swap) return;
    updateMutation.mutate({ id: d.id, data: { sortOrder: swap.sortOrder } });
    updateMutation.mutate({ id: swap.id, data: { sortOrder: d.sortOrder } });
  };

  const formFields = (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label className="label">Label</label>
        <input
          className="input w-full"
          value={form.label}
          onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
          placeholder={meta.placeholder}
          autoFocus
        />
      </div>
      <div>
        <label className="label">Color</label>
        <select
          className="select w-full"
          value={TONE_OPTIONS.some(t => t.value === form.color) ? form.color : 'muted'}
          onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
        >
          {TONE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
      <label className="flex items-center gap-2 text-sm sm:col-span-2">
        <input
          type="checkbox"
          checked={form.isDefault}
          onChange={e => setForm(f => ({ ...f, isDefault: e.target.checked }))}
        />
        <span>Use as the default {meta.label.toLowerCase()} for new tickets</span>
      </label>
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="section-title">Classification</h3>
        <p className="text-sm text-muted">
          The pick-lists behind a ticket&apos;s Type, Priority, Source, Business impact and
          Environment. Turning a value off keeps it on the tickets that already have it — it
          just stops being offered on new ones.
        </p>
      </div>

      {/* Which list is being edited */}
      <div className="flex flex-wrap gap-1.5">
        {KINDS.map(k => {
          const count = all.filter(d => d.kind === k.kind && d.isActive).length;
          const on = k.kind === kind;
          return (
            <button
              key={k.kind}
              type="button"
              onClick={() => { setKind(k.kind); reset(); }}
              className="scpa-ghost-btn"
              style={{
                fontWeight: on ? 700 : 500,
                color: on ? 'var(--scpa-primary)' : 'var(--scpa-text-muted)',
                borderBottom: on ? '2px solid var(--scpa-primary)' : '2px solid transparent',
                borderRadius: 0,
              }}
            >
              {k.label}
              <span style={{ fontSize: 11, opacity: 0.7, marginLeft: 4 }}>{count}</span>
            </button>
          );
        })}
      </div>

      <p className="text-sm text-muted">{meta.blurb}</p>

      {isLoading ? (
        <div className="text-sm text-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState>
          No {meta.label.toLowerCase()} values yet — <strong>Add {meta.label.toLowerCase()}</strong> below
          to give people something to pick.
        </EmptyState>
      ) : (
        <div className="flex flex-col gap-1.5">
          {rows.map((d, i) => (
            <div key={d.id} className="card" style={{ padding: 10 }}>
              {editingId === d.id ? (
                <div className="flex flex-col gap-3">
                  {formFields}
                  <div className="flex gap-2">
                    <button type="button" className="btn btn-primary" onClick={() => saveEdit(d)} disabled={updateMutation.isPending}>
                      <Check className="w-3.5 h-3.5" /> Save
                    </button>
                    <button type="button" className="btn btn-ghost" onClick={reset}>
                      <X className="w-3.5 h-3.5" /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Order */}
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => move(d, -1)}
                      disabled={i === 0}
                      title="Move up"
                      style={{ background: 'none', border: 'none', cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? 0.3 : 1, padding: 0, color: 'var(--scpa-text-muted)' }}
                    >
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(d, 1)}
                      disabled={i === rows.length - 1}
                      title="Move down"
                      style={{ background: 'none', border: 'none', cursor: i === rows.length - 1 ? 'default' : 'pointer', opacity: i === rows.length - 1 ? 0.3 : 1, padding: 0, color: 'var(--scpa-text-muted)' }}
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  <span style={{ fontWeight: 700, color: toneCss(d.color), opacity: d.isActive ? 1 : 0.5 }}>
                    {d.label}
                  </span>

                  {d.isDefault && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                      style={{ background: 'var(--scpa-primary-muted)', color: 'var(--scpa-primary)' }}>
                      default
                    </span>
                  )}
                  {!d.isActive && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                      style={{ background: 'var(--scpa-surface-alt)', color: 'var(--scpa-text-muted)' }}>
                      off
                    </span>
                  )}
                  {d.isSystem && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-semibold"
                      style={{ color: 'var(--scpa-text-muted)' }}
                      title="ctTicketing sets this value itself — it can be renamed and hidden, but not deleted"
                    >
                      <Lock className="w-3 h-3" /> built-in
                    </span>
                  )}

                  <span className="text-xs text-muted" style={{ marginLeft: 'auto' }}>
                    {d.ticketCount ?? 0} ticket{(d.ticketCount ?? 0) === 1 ? '' : 's'}
                  </span>

                  {/* Enable / disable */}
                  <label className="flex items-center gap-1.5 text-xs" title={d.isActive ? 'Offered on new tickets' : 'Hidden from new tickets'}>
                    <input
                      type="checkbox"
                      checked={d.isActive}
                      onChange={e => updateMutation.mutate({ id: d.id, data: { isActive: e.target.checked } })}
                    />
                    <span>{d.isActive ? 'On' : 'Off'}</span>
                  </label>

                  <button type="button" className="scpa-ghost-btn" onClick={() => startEdit(d)} title="Edit">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    className="scpa-ghost-btn"
                    onClick={() => deleteMutation.mutate(d.id)}
                    disabled={d.isSystem}
                    title={d.isSystem ? 'Built-in values cannot be deleted — turn it off instead' : 'Delete'}
                    style={{ color: d.isSystem ? 'var(--scpa-text-muted)' : 'var(--scpa-status-error)', opacity: d.isSystem ? 0.4 : 1 }}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <div className="card flex flex-col gap-3" style={{ padding: 12 }}>
          {formFields}
          <div className="flex gap-2">
            <button type="button" className="btn btn-primary" onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>
              <Check className="w-3.5 h-3.5" /> Add {meta.label.toLowerCase()}
            </button>
            <button type="button" className="btn btn-ghost" onClick={reset}>
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <div>
          <button type="button" className="btn btn-secondary" onClick={() => { setEditingId(null); setForm(EMPTY_FORM); setAdding(true); }}>
            <Plus className="w-3.5 h-3.5" /> Add {meta.label.toLowerCase()}
          </button>
        </div>
      )}
    </div>
  );
}
